import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { localizeCurrencyToWholeNumber, parseCurrencyToFloat } from "src/utils";
import debounce from "lodash.debounce";
import MaskedInput from "react-text-mask";
import useSelector from "src/utils/useSelector";
import { createNumberMask } from "text-mask-addons";

interface CurrencyInputProps {
    className?: string;
    customWrapperClass?: string;
    value?: number;
    maxValue?: number;
    minValue?: number;
    step?: string;
    updateValue: (newValue: number, localizedDisplay: string) => void;
    debounceUpdates?: boolean;
}

const Wrapper = styled("div")`
    display: flex;
    padding: 0 1em;
    width: 100%;
`;

const StyledInput = styled(MaskedInput)`
    border: none;
    flex-grow: 2;
    font-size: 1em;
    height: auto;
    margin-top: 0;
    min-width: auto;
    outline: none;
    width: 90%;
`;
/**
 * International-friendly number input for use with currency.
 * @param className - Custom CSS class to add to input.
 * @param customWrapperClass - Custom CSS class to add to wrapper.
 * @param initialValue - Sets the initial input value.
 * @param maxValue - Maximum currency amount.
 * @param minValue - Amount to not allow input to dip below.
 * @param step - Amount the value increases or decreases by for users using arrows / directions.
 * @param updateValue - Runs after user unfocuses input.  Passes both the raw number and the localized currency string
 */
const CurrencyInput = ({
    className = "",
    customWrapperClass = "",
    value = 0,
    maxValue,
    minValue = 0,
    step = "500",
    updateValue,
    debounceUpdates = false,
}: CurrencyInputProps) => {
    const country = useSelector((state) => state.account.address?.country);

    const [displayCurrency, setDisplayCurrency] = useState(localizeCurrencyToWholeNumber(value));

    const handleUserInputCurrency = (e: any) => {
        e.persist();
        setDisplayCurrency(e.target.value);

        if (debounceUpdates) {
            debouncedHandleChange(e.target.value);
        }
    };

    useEffect(() => {
        setDisplayCurrency(localizeCurrencyToWholeNumber(value));
    }, [value]);

    /**
     * This function is where we do some sanitization and run updates, including onUpdate.
     * Is only fired once a user unfocuses the input
     */
    const handleBlur = (event: any) => {
        if (debounceUpdates === false) {
            handleUpdate(event.target.value);
        }
    };

    const handleUpdate = (newValue: string) => {
        let displayValue = newValue;
        let parsedValue = parseCurrencyToFloat(displayValue);

        // Checks if a user-typed value is over the allowed value.
        if (maxValue && parsedValue >= maxValue) {
            displayValue = localizeCurrencyToWholeNumber(maxValue);
            parsedValue = maxValue;
        }

        // Checks if a user-typed value is under the allowed value.
        if (parsedValue <= minValue) {
            displayValue = localizeCurrencyToWholeNumber(minValue);
            parsedValue = minValue;
        }

        setDisplayCurrency(displayValue);

        updateValue(parsedValue, displayValue);
    };

    // Allows users to enter values by hitting enter.
    const handleKeyDown = (e: any) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleUpdate(e.target.value);
        }
    };

    const debouncedHandleChange = useCallback(
        debounce((newValue: string) => {
            handleUpdate(newValue);
        }, 2000),
        []
    );

    return (
        <Wrapper className={customWrapperClass}>
            <StyledInput
                className={className}
                inputMode="numeric"
                mask={createNumberMask({
                    prefix: `${country === "CA" ? "CA$" : "$"}`,
                    integerLimit: `${maxValue}`.length,
                })}
                guide={false}
                value={displayCurrency}
                onBlur={handleBlur}
                onChange={handleUserInputCurrency}
                onKeyDown={handleKeyDown}
                step={step}
                min={minValue}
                max={maxValue}
            />
        </Wrapper>
    );
};

export { CurrencyInput };
